"use client";

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Icon } from "@/components/ui/icon";
import {
  useJobWizardStore,
  type WizardUploadItem,
} from "@/features/jobs/store/job-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useIsMobile from "@/hooks/use-is-mobile";
import { uploadImportFile } from "@/features/jobs/components/view/pipeline/upload-import-file";
import {
  resolveImportStageId,
  toImportStages,
} from "@/features/jobs/components/profile-import/import-stage";
import { ProfileImportStageField } from "@/features/jobs/components/profile-import/profile-import-stage-field";
import { useListImports } from "@/lib/api/generated";
import type { ProfileImportItem } from "@/lib/api/model/profileImportItem";
import { ApiError } from "@/lib/api/error";
import {
  getPermissionErrorMessage,
  isPermissionDenied,
} from "@/lib/permissions";

const ACCEPTED_EXTS = [
  ".pdf",
  ".docx",
  ".rtf",
  ".txt",
  ".png",
  ".jpeg",
  ".jpg",
  ".webp",
];
const ACCEPT_ATTR = ACCEPTED_EXTS.join(",");
const MAX_FILE_MB = 10;

function validateFile(file: File): string | null {
  const ext = "." + (file.name.split(".").pop()?.toLowerCase() ?? "");
  if (!ACCEPTED_EXTS.includes(ext))
    return `Unsupported type (${ext || "no extension"})`;
  if (file.size === 0) return "File is empty";
  if (file.size > MAX_FILE_MB * 1024 * 1024) return `Exceeds ${MAX_FILE_MB}MB`;
  return null;
}

function makeItemId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`;
}

function FileUploadCard({
  item,
  isFirst,
  isLast,
  onRemove,
  onReupload,
}: {
  item: WizardUploadItem;
  isFirst: boolean;
  isLast: boolean;
  onRemove: () => void;
  onReupload?: () => void;
}) {
  const cfg = useMemo(() => {
    if (item.state === "uploaded") {
      return {
        bgClass: "bg-[rgba(22,163,74,0.1)] dark:bg-[rgba(102,220,126,0.1)]",
        textClass: "text-[#15803d] dark:text-[#66dc7e]",
        label: "Uploaded",
      };
    }
    if (item.state === "failed") {
      return {
        bgClass: "bg-destructive/10 dark:bg-[rgba(233,115,117,0.08)]",
        textClass: "text-destructive dark:text-[#e97375]",
        label: "Failed",
      };
    }
    return {
      bgClass: "bg-[rgba(160,122,0,0.1)] dark:bg-[rgba(228,217,92,0.1)]",
      textClass: "text-[#a07a00] dark:text-[#e4d95c]",
      label: "Uploading",
    };
  }, [item.state]);

  const sizeStr = `${(item.file.size / (1024 * 1024)).toFixed(1)} MB`;

  const row = (
    <div
      className={`flex gap-2 items-center px-2 py-2.5 border-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] bg-surface-elevated ${
        isFirst && isLast
          ? "border rounded-lg"
          : isFirst
            ? "border rounded-t-lg border-b-0"
            : isLast
              ? "border rounded-b-lg"
              : "border-l border-r border-t"
      }`}
      style={
        item.state === "failed"
          ? {
              backgroundImage:
                "linear-gradient(90deg, rgba(233,115,117,0.08) 0%, rgba(233,115,117,0.08) 100%), linear-gradient(90deg, var(--surface-elevated) 0%, var(--surface-elevated) 100%)",
            }
          : undefined
      }
    >
      <Icon
        name="IconFileText"
        size={16}
        className="text-heading opacity-50 shrink-0"
      />

      <div className="flex flex-1 gap-1.5 items-center min-w-0">
        <span className="text-sm font-medium text-contrast/80 truncate leading-5">
          {item.file.name}
        </span>
        <div className="size-1 rounded-full bg-contrast/20 shrink-0" />
        <span className="text-xs text-contrast/50 shrink-0">{sizeStr}</span>
      </div>

      <div
        className={cn(
          "backdrop-blur-[2px] border border-border flex items-center justify-center overflow-hidden p-0.5 rounded-md shrink-0",
          cfg.bgClass,
        )}
      >
        <span className={cn("text-xs font-medium px-1", cfg.textClass)}>
          {cfg.label}
        </span>
      </div>

      {item.state === "failed" && onReupload && (
        <button
          type="button"
          onClick={onReupload}
          className="flex items-center gap-1 cursor-pointer shrink-0"
        >
          <Icon name="IconCloudUpload" size={13} className="text-heading" />
          <span className="text-xs text-heading">Reupload</span>
        </button>
      )}

      <button
        type="button"
        onClick={onRemove}
        className="flex items-center justify-center size-5 rounded-[5px] cursor-pointer shrink-0"
      >
        <Icon
          name="IconCrossMedium"
          size={13}
          className="text-contrast/30 hover:text-contrast/60"
        />
      </button>
    </div>
  );

  // Only show tooltip on failed rows — hover anywhere on the row reveals the
  // upload failure reason (network error, permission denied, server rejection).
  if (item.state === "failed" && item.error) {
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-80">
          {item.error}
        </TooltipContent>
      </Tooltip>
    );
  }

  return row;
}

function ClonedImportsBanner({ imports }: { imports: ProfileImportItem[] }) {
  if (imports.length === 0) return null;

  return (
    <div className="rounded-md border border-primary/10 bg-primary/10 px-3.5 py-3">
      <p className="text-sm leading-snug text-primary dark:text-[#bccdff]">
        {imports.length} import{imports.length === 1 ? "" : "s"} copied — add
        more below.
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {imports.slice(0, 5).map((item) => (
          <li
            key={item.id}
            className="truncate text-sm text-contrast/70"
            title={item.source_filename ?? item.full_name ?? item.id}
          >
            {item.source_filename ?? item.full_name ?? "Imported profile"}
          </li>
        ))}
        {imports.length > 5 && (
          <li className="text-sm text-contrast/50">
            +{imports.length - 5} more
          </li>
        )}
      </ul>
    </div>
  );
}

export function StepImport() {
  const { form, updateForm, createdJobId, mode } = useJobWizardStore();
  const isCloneMode = mode === "clone";
  const { data: clonedImportsData } = useListImports(
    createdJobId ?? "",
    undefined,
    {
      query: {
        enabled: Boolean(isCloneMode && createdJobId),
      },
    },
  );
  const clonedImports = clonedImportsData?.imports ?? [];
  const isMobile = useIsMobile();
  const [dragOver, setDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const ITEMS_PER_PAGE = 6;

  const importStages = useMemo(
    () => toImportStages(form.stages),
    [form.stages],
  );

  // Seed / re-seed importStageId when empty or invalid (e.g. after step-2 sync).
  useEffect(() => {
    const resolved = resolveImportStageId(importStages, form.importStageId);
    if (resolved !== (form.importStageId ?? "")) {
      updateForm("importStageId", resolved || null);
    }
  }, [importStages, form.importStageId, updateForm]);

  const selectedStageName = useMemo(() => {
    if (!form.importStageId) return null;
    return importStages.find((s) => s.id === form.importStageId)?.name ?? null;
  }, [form.importStageId, importStages]);

  const landInCopy = selectedStageName
    ? `they'll land in ${selectedStageName}.`
    : "they'll land in the selected stage.";

  // Keep a live ref to uploadedFiles so async upload callbacks can read
  // the freshest list without re-running effects or capturing stale state.
  const itemsRef = useRef(form.uploadedFiles);
  useEffect(() => {
    itemsRef.current = form.uploadedFiles;
  }, [form.uploadedFiles]);

  const patchItem = useCallback(
    (id: string, patch: Partial<WizardUploadItem>) => {
      const next = itemsRef.current.map((i) =>
        i.id === id ? { ...i, ...patch } : i,
      );
      itemsRef.current = next;
      updateForm("uploadedFiles", next);
    },
    [updateForm],
  );

  // stageId is captured at call time so mid-batch selection changes only
  // affect new uploads; already-uploaded files keep their original stage_id.
  const beginUpload = useCallback(
    (item: WizardUploadItem, stageId: string) => {
      if (!createdJobId || !stageId) return;
      const controller = new AbortController();
      patchItem(item.id, {
        controller,
        state: "uploading",
        progress: 0,
        error: undefined,
      });

      uploadImportFile({
        jobId: createdJobId,
        stageId,
        file: item.file,
        signal: controller.signal,
        onProgress: (pct) => patchItem(item.id, { progress: pct }),
      })
        .then((resp) => {
          const rejected = resp?.rejected ?? [];
          if (rejected.length > 0) {
            patchItem(item.id, {
              state: "failed",
              progress: 0,
              error: rejected[0]?.reason || "Rejected by server",
            });
            return;
          }
          const first = resp?.imports?.[0];
          patchItem(item.id, {
            state: "uploaded",
            progress: 100,
            profileImportId: first?.id,
          });
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") {
            return;
          }
          if (err instanceof ApiError && isPermissionDenied(err)) {
            patchItem(item.id, {
              state: "failed",
              progress: 0,
              error: getPermissionErrorMessage(err),
            });
            return;
          }
          const message = err instanceof Error ? err.message : "Upload failed";
          patchItem(item.id, {
            state: "failed",
            progress: 0,
            error: message,
          });
        });
    },
    [createdJobId, patchItem],
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const stageId = useJobWizardStore.getState().form.importStageId;
      if (!stageId) return;

      const list = Array.from(files);
      const accepted: WizardUploadItem[] = [];
      for (const file of list) {
        if (validateFile(file)) continue; // silently skip — UX is lenient here
        accepted.push({
          id: makeItemId(file),
          file,
          state: "uploading",
          progress: 0,
        });
      }
      if (!accepted.length) return;

      const next = [...itemsRef.current, ...accepted];
      itemsRef.current = next;
      updateForm("uploadedFiles", next);
      // Capture current selection once for this batch of new files.
      for (const it of accepted) beginUpload(it, stageId);
    },
    [updateForm, beginUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const removeFile = useCallback(
    (globalIdx: number) => {
      const list = itemsRef.current;
      const target = list[globalIdx];
      if (!target) return;
      if (target.state === "uploading") target.controller?.abort();
      const next = list.filter((_, i) => i !== globalIdx);
      itemsRef.current = next;
      updateForm("uploadedFiles", next);
    },
    [updateForm],
  );

  const reuploadFile = useCallback(
    (globalIdx: number) => {
      const list = itemsRef.current;
      const target = list[globalIdx];
      if (!target) return;
      const stageId = useJobWizardStore.getState().form.importStageId;
      if (!stageId) return;
      beginUpload(target, stageId);
    },
    [beginUpload],
  );

  const filteredFiles = useMemo(() => {
    const base = searchQuery
      ? form.uploadedFiles.filter((f) =>
          f.file.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : form.uploadedFiles;
    // Surface failed rows at the top so the user can triage them without
    // hunting through successful uploads. Preserve insertion order otherwise.
    const stateRank: Record<WizardUploadItem["state"], number> = {
      failed: 0,
      uploading: 1,
      uploaded: 2,
    };
    return [...base].sort((a, b) => stateRank[a.state] - stateRank[b.state]);
  }, [form.uploadedFiles, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredFiles.length / ITEMS_PER_PAGE),
  );
  const pagedFiles = isMobile
    ? filteredFiles
    : filteredFiles.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      );

  const hasFiles = form.uploadedFiles.length > 0;

  const counts = useMemo(() => {
    const uploading = form.uploadedFiles.filter(
      (f) => f.state === "uploading",
    ).length;
    const uploaded = form.uploadedFiles.filter(
      (f) => f.state === "uploaded",
    ).length;
    const failed = form.uploadedFiles.filter(
      (f) => f.state === "failed",
    ).length;
    return {
      uploading,
      uploaded,
      failed,
      total: form.uploadedFiles.length,
    };
  }, [form.uploadedFiles]);

  const anyUploading = counts.uploading > 0;
  const canUpload = Boolean(form.importStageId);
  const progressPct =
    counts.total === 0
      ? 0
      : Math.min(
          100,
          Math.round(((counts.uploaded + counts.failed) / counts.total) * 100),
        );

  const stageField = (
    <ProfileImportStageField
      stages={importStages}
      value={form.importStageId ?? ""}
      onChange={(id) => updateForm("importStageId", id)}
      disabled={anyUploading}
    />
  );

  const atsBanner =
    form.sourcingType === "ats_import" ? (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-contrast/[0.02] px-3 py-2">
        <Icon
          name="IconLoadingCircle"
          size={14}
          className="text-contrast/50 animate-spin"
        />
        <span className="text-xs text-contrast/60 leading-4">
          Candidates are importing from your ATS in the background. Add any
          extra profiles here — {landInCopy}
        </span>
      </div>
    ) : null;

  const hiddenInputs = (
    <>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        className="hidden"
        disabled={!canUpload}
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={folderRef}
        type="file"
        {...({
          webkitdirectory: "",
        } as React.InputHTMLAttributes<HTMLInputElement>)}
        accept={ACCEPT_ATTR}
        className="hidden"
        disabled={!canUpload}
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col gap-4">
        {isCloneMode && <ClonedImportsBanner imports={clonedImports} />}
        {hiddenInputs}

        {atsBanner}
        {stageField}

        {!hasFiles ? (
          <button
            type="button"
            onClick={() => canUpload && fileRef.current?.click()}
            disabled={!canUpload}
            className="flex flex-col items-center justify-center gap-3 w-full rounded-2xl border border-dashed border-border bg-surface-inset py-10 px-6 active:bg-contrast/4 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <div className="opacity-30">
              <Icon
                name="IconCloudDownload"
                size={40}
                className="text-foreground"
              />
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-sm font-medium text-heading">
                Tap to upload files
              </span>
              <span className="text-xs text-contrast/40 text-center leading-4">
                PDF, DOCX, RTF, TXT, PNG or JPEG · up to 10MB
              </span>
            </div>
          </button>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-heading">
                {counts.total} {counts.total === 1 ? "file" : "files"} added
              </span>
              <button
                type="button"
                onClick={() => canUpload && fileRef.current?.click()}
                disabled={!canUpload}
                className="flex items-center gap-1 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Icon name="IconPlusSmall" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">
                  Add more
                </span>
              </button>
            </div>

            <div className="flex flex-col">
              {pagedFiles.map((item, i) => {
                const globalIdx = form.uploadedFiles.indexOf(item);
                return (
                  <FileUploadCard
                    key={item.id}
                    item={item}
                    isFirst={i === 0}
                    isLast={i === pagedFiles.length - 1}
                    onRemove={() => removeFile(globalIdx)}
                    onReupload={() => reuploadFile(globalIdx)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {atsBanner}
      {stageField}

      {isCloneMode && <ClonedImportsBanner imports={clonedImports} />}
      <div
        className={`bg-surface-inset border p-4 border-dashed rounded-3xl flex flex-col items-center justify-center transition-colors ${
          hasFiles ? "h-[216px] shrink-0" : "flex-1"
        } ${dragOver ? "border-primary bg-primary/5" : "border-border"} ${
          !canUpload ? "opacity-60 pointer-events-none" : ""
        }`}
        onDragOver={(e) => {
          if (!canUpload) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="flex flex-col gap-4 items-center justify-center px-4">
          <div className="opacity-30">
            <Icon
              name="IconCloudDownload"
              size={48}
              className="text-foreground"
            />
          </div>

          <div className="flex gap-3 items-start">
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              variant="light"
              size="sm"
              disabled={!canUpload}
              icon={
                <Icon
                  name="IconCloudUpload"
                  size={16}
                  className="text-white dark:text-[#171717]"
                />
              }
            >
              Upload File
            </Button>
            <Button
              type="button"
              onClick={() => folderRef.current?.click()}
              variant="light"
              size="sm"
              disabled={!canUpload}
              icon={
                <Icon
                  name="IconFolderUpload"
                  size={16}
                  className="text-white dark:text-[#171717]"
                />
              }
            >
              Upload Folder
            </Button>
          </div>

          <div className="flex flex-col gap-2 items-center">
            <p className="text-sm text-contrast/60 tracking-[-0.14px]">
              Uploading a folder will automatically upload all the files inside
            </p>
            <div className="bg-contrast/8 rounded-[68px] px-3 py-[3px]">
              <span className="text-xs font-medium text-contrast/40 tracking-[-0.36px]">
                Supports .pdf, .docx, .rtf, .txt, .png & .jpeg files up to 10MB
                and 20 pages
              </span>
            </div>
          </div>
        </div>
      </div>

      {hiddenInputs}

      {hasFiles && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="bg-contrast/8 flex h-7 items-center justify-center overflow-hidden rounded-lg w-[183px]">
              <div className="flex flex-1 gap-0 items-center justify-center overflow-hidden px-2 py-1.5">
                <div className="flex flex-1 gap-0.5 items-center">
                  <Icon
                    name="IconMagnifyingGlass"
                    size={16}
                    className="text-heading opacity-50"
                  />
                  <Input
                    type="text"
                    variant="ghost"
                    size="none"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="flex-1 px-1"
                  />
                </div>
              </div>
            </div>

            <div className="bg-contrast/3 border border-contrast/5 flex gap-1.5 h-7 items-center p-1.5 rounded-md">
              <Icon
                name={anyUploading ? "IconLoadingCircle" : "IconCircleCheck"}
                size={16}
                className={
                  anyUploading
                    ? "text-heading opacity-50 animate-spin"
                    : "text-[#15803d] dark:text-[#66dc7e] opacity-80"
                }
              />
              <div className="flex gap-2 items-center">
                <span className="text-sm font-medium text-heading leading-5">
                  {anyUploading ? "Uploading" : "Complete"}
                </span>
                <div className="bg-contrast/15 rounded-full h-1.5 w-20 overflow-hidden">
                  <div
                    className="bg-contrast/85 h-full transition-[width] duration-200"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <p className="text-sm leading-5">
                  <span className="text-contrast/85">
                    {counts.uploaded + counts.failed}{" "}
                  </span>
                  <span className="text-contrast/60">/ {counts.total}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            {pagedFiles.map((item, i) => {
              const globalIdx = form.uploadedFiles.indexOf(item);
              return (
                <FileUploadCard
                  key={item.id}
                  item={item}
                  isFirst={i === 0}
                  isLast={i === pagedFiles.length - 1}
                  onRemove={() => removeFile(globalIdx)}
                  onReupload={() => reuploadFile(globalIdx)}
                />
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between w-full">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="bg-contrast/8 flex items-center justify-center min-h-8 min-w-8 overflow-hidden rounded-md disabled:opacity-40 cursor-pointer"
              >
                <Icon
                  name="IconChevronLeft"
                  size={20}
                  className="text-heading opacity-60"
                />
              </button>

              <div className="flex gap-1 items-center">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number | string;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (i < 3) {
                    pageNum = i + 1;
                  } else if (i === 3) {
                    pageNum = "...";
                  } else {
                    pageNum = totalPages;
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        typeof pageNum === "number" && setCurrentPage(pageNum)
                      }
                      className={`flex flex-col items-center justify-center min-h-8 min-w-8 overflow-hidden rounded-md w-8 cursor-pointer ${
                        pageNum === currentPage
                          ? "bg-contrast/5 border border-contrast/15 text-heading"
                          : "text-contrast/50 hover:text-contrast/80"
                      }`}
                    >
                      <span className="text-sm font-medium leading-5">
                        {pageNum}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="bg-contrast/8 flex items-center justify-center min-h-8 min-w-8 overflow-hidden rounded-md disabled:opacity-40 cursor-pointer"
              >
                <Icon
                  name="IconChevronRight"
                  size={20}
                  className="text-heading opacity-60"
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
